// One person in the call: their video (or initials), name and mic/cam state.

import { useEffect, useRef, useState, type ReactNode } from 'react';

export function VideoTile({
  stream,
  name,
  self,
  mic,
  cam,
  state,
  actions,
}: {
  stream: MediaStream | null;
  name: string;
  self?: boolean;
  mic: boolean;
  cam: boolean;
  /** Connection state to this peer (remote tiles). */
  state?: string;
  actions?: ReactNode;
}) {
  const video = useRef<HTMLVideoElement>(null);
  // Browsers refuse to play sound without a click on the page; if that
  // happens the guest would hear nothing, so offer the click.
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    el.srcObject = stream;
    if (!stream || self) return;
    el.play()?.catch((err: unknown) => {
      if ((err as { name?: string }).name === 'NotAllowedError') setBlocked(true);
    });
  }, [stream, self]);
  const unblock = () => {
    void video.current?.play().then(() => setBlocked(false), () => undefined);
  };

  const showVideo = !!stream && cam && stream.getVideoTracks().length > 0;
  const connecting = !self && state !== undefined && state !== 'connected';

  return (
    <figure
      data-peer-state={state}
      className="relative aspect-video overflow-hidden rounded-xl bg-neutral-900 text-white"
      aria-label={self ? `${name} (you)` : name}
    >
      {/* Remote audio plays through this element; our own is muted. */}
      <video ref={video} autoPlay playsInline muted={self} className={`h-full w-full object-cover ${showVideo ? '' : 'invisible'} ${self ? '-scale-x-100' : ''}`} />
      {!showVideo && (
        <div className="absolute inset-0 flex items-center justify-center text-3xl font-semibold">
          {name
            .split(/\s+/)
            .map((w) => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()}
        </div>
      )}
      <figcaption className="absolute bottom-2 left-2 flex items-center gap-2 rounded bg-black/60 px-2 py-1 text-xs">
        <span>{self ? `${name} (you)` : name}</span>
        {!mic && <span aria-label="Muted">🔇</span>}
        {connecting && <span className="text-amber-300">{state === 'failed' ? 'reconnecting…' : 'connecting…'}</span>}
      </figcaption>
      {blocked && (
        <button
          type="button"
          onClick={unblock}
          className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-medium"
        >
          🔈 Click to hear {name}
        </button>
      )}
      {actions && <div className="absolute right-2 top-2">{actions}</div>}
    </figure>
  );
}
